import { FetchAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { HistoryService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Endpoint } from '../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../constants/routes';
import { ListPendingParams } from '../../../../interfaces/exam-collect/pending-exam-collection';
import { ExamCollectionUtils } from '../../../../utils/exam-collection-utils';
import ToastHandler from './pending-exam-collection-toast-handler';

const historyService = HistoryService;

interface ExamCollectionWithoutBarcodeHandlerPureParameters {
  paramPreviousDateExams?: string,
  paramRequestPassword?: string,
  paramUseLaboratorySampleStandard?: string
}

class ExamCollectionWithoutBarcodeHandler {
  static initialize(pureParameters: ExamCollectionWithoutBarcodeHandlerPureParameters): void {
    this.parameters = {
      requestPassword: this.getDefaultValue(pureParameters.paramRequestPassword) === 'S',
      useLaboratorySampleStandard: this.getDefaultValue(pureParameters.paramUseLaboratorySampleStandard) === 'S',
      usePreviousDateExams: this.getDefaultValue(pureParameters.paramPreviousDateExams) === 'S',
    }
    this.selectedExams = [];
  }

  private static getDefaultValue(param?: string): string {
    return param ? param : 'S';
  }

  private static parameters = {
    requestPassword: false,
    useLaboratorySampleStandard: false,
    usePreviousDateExams: false,
  }

  private static selectedExams: ListPendingParams[] = [];

  static clearSelectedExams(): void {
    this.selectedExams = [];
  }

  static getSelectedExams(): ListPendingParams[] {
    return [...this.selectedExams];
  }

  static userCanSubmitExamSubsetList(): boolean {
    return this.parameters.useLaboratorySampleStandard
            && this.parameters.usePreviousDateExams;
  }

  static selectExam(enabled: boolean, card: ListPendingParams): void {
    if (enabled) {
      this.selectedExams.push(card);
    } else {
      const index = this.selectedExams.findIndex(item => item.nrSequencia === card.nrSequencia);
      this.selectedExams.splice(index, 1);
    }
  }

  static submitToCollection(fetchPendingExam: FetchAPI): void {
    if(this.userCanSubmitExamSubsetList() && this.selectedExams.length === 0) {
      ToastHandler.showToast('pending.exam-collection-not-found', ToastLevel.WARNING);
      return;
    }

    if (this.userCanSubmitExamSubsetList() && !this.parameters.requestPassword) {
      this.updateStatusWithoutConfirmation(fetchPendingExam);
      return;
    }

    this.updateStatusWithConfirmation();
  }

  static createUpdateExamsBody(encounterNumber: string | number, prescriptionNumber: string | number): Promise<Record<string, Value>> {
    if (this.userCanSubmitExamSubsetList()) {
      return Promise.resolve({
        exams: this.selectedExams,
        nrPrescription: prescriptionNumber,
      });
    }

    return this.getAllExamsInPrescritpion(encounterNumber, prescriptionNumber)
      .then((response) => {
        return {
          exams: response,
          nrPrescription: prescriptionNumber,
        }
      });
  }

  private static updateStatusWithoutConfirmation(fetchPendingExam: FetchAPI): Promise<void> {
    const { encounterId, prescriptionId } = historyService.getCurrentRouteParams();

    return this.createUpdateExamsBody(encounterId as number, prescriptionId as number).then(body => {
      return HttpService.post(Endpoint.UPDATE_STATUS_PRESCRIPRION, {}, { body }).then(response => {
        if (response.status === 200) {
          this.handleUpdateSuccess(fetchPendingExam);
        }
      });
    }).catch(err => {
      ToastHandler.showToast(err?.body.errorMessage, ToastLevel.ERROR);
    })
  }

  private static handleUpdateSuccess(fetchPendingExam: FetchAPI): void {
    ToastHandler.showToast('pending.exam-collection-stats', ToastLevel.SUCCESS);
    this.clearSelectedExams();
    ExamCollectionUtils.updateExamsOrRedirect(fetchPendingExam);
  }

  static async getAllExamsInPrescritpion(encounterNumber: string | number, prescriptionNumber: string | number): Promise<ListPendingParams[]> {
    const response = await HttpService.get(Endpoint.EXAM_COLLECT_LIST_PENDING, {
      encounterNumber,
      fullList: true,
      prescriptionNumber
    });
    return response.body as ListPendingParams[];
  }

  private static updateStatusWithConfirmation(): void {
    const { prescriptionId, patientId, encounterId } = historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.EXAM_COLLECT_CONFIRM;

    applicationContext.redirect(redirectRoute, {
      encounterId,
      patientId,
      prescriptionNumber: prescriptionId,
    });
  }
}

export {
  ExamCollectionWithoutBarcodeHandler
};